import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { ImageViewer } from "../ImageViewer";
import { utils } from "../../utils";
import s from "./index.module.css";

export function ProductWithImage({
  product,
  idx,
  expandedIndex,
  setExpandedIndex,
}) {
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const isExpanded = expandedIndex === idx;
  const [showReadMore, setShowReadMore] = useState({
    short: false,
    long: false,
  });

  const shortDescRef = useRef(null);
  const longDescRef = useRef(null);
  const productCardRef = useRef(null);

  // Memoized computed values
  const allImages = useMemo(
    () => product.images?.map((img) => utils.getImageUrl(img)) || [],
    [product.images],
  );

  const firstImage = allImages[0];

  const price = product.selling_price || 0;
  const discounted = product.discounted_price || 0;
  const discountPercentage = useMemo(
    () => (price > 0 ? Math.round(((price - discounted) / price) * 100) : 0),
    [price, discounted],
  );

  const formatPrice = useCallback((amount) => `₹${amount.toFixed(2)}`, []);

  const handleImageClick = useCallback(() => {
    if (allImages.length > 0) {
      setIsImageViewerOpen(true);
    }
  }, [allImages.length]);

  const handleCloseImageViewer = useCallback(() => {
    setIsImageViewerOpen(false);
  }, []);

  const handleReadMoreClick = useCallback(() => {
    setExpandedIndex(idx);
  }, [idx, setExpandedIndex]);

  const handleReadLessClick = useCallback(() => {
    setExpandedIndex(null);
  }, [setExpandedIndex]);

  useEffect(() => {
    const checkTextOverflow = () => {
      const updates = {};

      if (shortDescRef.current) {
        const el = shortDescRef.current;
        updates.short = el.scrollHeight > el.clientHeight;
      }

      if (longDescRef.current) {
        const el = longDescRef.current;
        updates.long = el.scrollHeight > el.clientHeight;
      }

      setShowReadMore((prev) => ({ ...prev, ...updates }));
    };

    checkTextOverflow();

    let timeoutId;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkTextOverflow, 150);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, [product.short_desc, product.long_desc]);

  const showPricing = discounted > 0 || price > 0;
  const showDiscount = discounted > 0 && price > 0 && discountPercentage > 0;
  const hasMultipleImages = allImages.length > 1;
  const showReadMoreButton =
    (showReadMore.short || showReadMore.long) &&
    (product.short_desc || product.long_desc);

  return (
    <>
      <li
        key={idx}
        className={`${s.productItem} ${isExpanded ? s.expanded : ""}`}
        ref={productCardRef}
      >
        <div className={s.productInfo}>
          {product.name && (
            <h3
              className={`${s.productTitle} ${isExpanded ? s.expandedTitle : ""}`}
            >
              {product.name}
            </h3>
          )}

          {showPricing && (
            <div className={s.productPricing}>
              {discounted > 0 && (
                <span className={s.productDiscounted}>
                  {formatPrice(discounted)}
                </span>
              )}
              {price > 0 && (
                <span className={s.productOriginal}>{formatPrice(price)}</span>
              )}
              {showDiscount && (
                <span className={s.productDiscount}>
                  {discountPercentage}% off
                </span>
              )}
            </div>
          )}

          {product.short_desc && (
            <div className={s.descriptionContainer}>
              <p
                ref={shortDescRef}
                className={`${s.productDescription} ${s.shortDescription} ${
                  isExpanded ? s.expanded : ""
                }`}
              >
                {product.short_desc}
              </p>
            </div>
          )}

          {product.long_desc && (
            <div className={s.descriptionContainer}>
              <p
                ref={longDescRef}
                className={`${s.productDescription} ${s.longDescription} ${
                  isExpanded ? s.expanded : ""
                }`}
              >
                {product.long_desc}
              </p>
            </div>
          )}

          {showReadMoreButton && (
            <button
              className={s.readMoreButton}
              onClick={isExpanded ? handleReadLessClick : handleReadMoreClick}
            >
              {isExpanded ? "Read Less" : "Read More"}
            </button>
          )}
        </div>

        {firstImage ? (
          <div className={s.imageContainer}>
            <button
              className={s.imageButton}
              onClick={handleImageClick}
              aria-label={`View ${product.name || "product"} images`}
              type="button"
            >
              <img
                src={firstImage}
                alt={product.name || "Product image"}
                className={s.productImage}
                loading="lazy"
              />
              {hasMultipleImages && (
                <span className={s.multiimage} aria-hidden="true">
                  +{allImages.length - 1}
                </span>
              )}

              {!product.is_stock_available && (
                <div
                  className={`${s.productStockUnavailable} ${
                    !firstImage ? s.productStockUnavailableWithoutImage : ""
                  }`}
                  aria-label="Out of stock"
                >
                  <span className={s.productStockUnavailableText}>
                    Out of Stock
                  </span>
                </div>
              )}
            </button>
          </div>
        ) : (
          <div
            className={s.productImagePlaceholder}
            aria-label="No image available"
          >
            No Image available
          </div>
        )}
      </li>

      {allImages.length > 0 && (
        <ImageViewer
          images={allImages}
          isOpen={isImageViewerOpen}
          onClose={handleCloseImageViewer}
          productName={product.name || ""}
        />
      )}
    </>
  );
}
